import React from "react";
import { Platform } from "react-native";
import { injectIntl } from "react-intl";
import { format as formatFn, parse as parseFn } from "date-fns";
import { Calendar } from "react-native-calendars";
import { withTheme } from "styled-components/native";
import { LocaleContext } from "cci-utility";
import Button from "../button/Button";
import DateFormat from "./DateFormat";
import Dialog from "../dialog/Dialog";
import Icon from "../icon/FontAwesomePro";
import TextField from "../field/TextField";

/**
 * Date selection field, which shows a calendar icon to the right of the text field.
 *
 * The calendar icon shows a calendar pop-up where a date can be selected.
 *
 * Props:
 * - parseFormat: Format used to parse the date string passed into the field, defaults to "yyyy-MM-dd". See date-fns for string patterns.
 * - format: Format used to display the date within the field, defaults to DateFormat.shortDate.
 */
class DateField extends React.PureComponent {
  constructor() {
    super();

    this.state = { showModal: false };
    this.onChangeDate = this.onChangeDate.bind(this);
    this.onChangeField = this.onChangeField.bind(this);
    this.onNativePress = this.onNativePress.bind(this);
  }

  /**
   * Fired when the date has been changed either by the user typing in a valid date or a selection has been made from the calendar.
   */
  onChangeDate(date) {
    const { parseFormat, onChangeText } = this.props;

    // Hide the calendar selection dialog
    this.setState({ showModal: false });

    // Update the selection date value
    onChangeText(formatFn(date, parseFormat));
  }

  /**
   * Fired when the user updates the text in the field.
   */
  onChangeField(text, locale) {
    const { intl, onChangeText, format, parseFormat } = this.props,
      date = parseFn(text, intl.formatMessage(format), new Date(), { locale }),
      regex = /(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/([12]\d{3})/g; // validates "MM/dd/yyyy"
    let stringDate = '';

    try {
      stringDate = formatFn(date, 'MM/dd/yyyy')
    } catch (error) {
      // A valid date has not been typed, but we update the state anyway because the input is controlled
      onChangeText(text);
    }

    if (!stringDate.match(regex)) {
      // A valid date has not been typed, but we update the state anyway because the input is controlled
      onChangeText(text);
      // } else if (
      //   Number(stringDate.substring(0, 2)) < 10 ||
      //   Number(stringDate.substring(3, 5)) < 10
      // ) {
      //   // A valid date has not been typed, but we update the state anyway because the input is controlled
      //   onChangeText(text);
    } else {
      // Update the selection date value
      this.onChangeDate(date);
    }
  }

  onNativePress() {
    this.setState({ showModal: true });
  }

  render() {
    const {
        intl,
        theme,
        parseFormat,
        format,
        editable,
        value,
        calendarProps,
        ...restProps
      } = this.props,
      isNative = Platform.OS !== "web",
      onChangeField = this.onChangeField;

    let formattedValue = value,
      dateValue = undefined;

    return (
      <LocaleContext.Consumer>
        {({ date: locale }) => {
          if (value) {
            // date-fns needs a date object in order to format the date
            dateValue = parseFn(value, parseFormat, new Date());

            if (isNaN(dateValue)) {
              // User has not finished typing or has typed in an invalid date, we undefine it to prevent an error in the Calendar
              dateValue = undefined;
            } else {
              // We have a valid date, so we format it accordingly
              formattedValue = formatFn(dateValue, intl.formatMessage(format), {
                locale
              });
            }
          }

          return (
            <React.Fragment>
              <TextField
                {...restProps}
                // Calendar icon is always shown we it's native
                rightComponent={
                  editable && (
                    <Button
                      leftIcon="calendar"
                      ui="link"
                      fontColor={theme.fieldFontColor || theme.fontColor}
                      onPress={() => this.setState({ showModal: true })}
                      style={{ paddingRight: 10 }}
                      disabled={restProps.disabled}
                    />
                  )
                }
                // For native this field is never editable
                editable={isNative ? false : editable}
                onNoEditablePress={
                  isNative && editable ? this.onNativePress : undefined
                }
                value={formattedValue}
                onChangeText={text => onChangeField(text, locale)}
              />

              {/* Date selection dialog */}
              <Dialog
                isVisible={this.state.showModal}
                autoSnap={false}
                width={300}
                paddings="5px"
                onBackdropPress={() => this.setState({ showModal: false })}
              >
                <Calendar
                  current={dateValue}
                  markedDates={{
                    [formatFn(new Date(), "yyyy-MM-dd")]: {
                      selected: true,
                      selectedColor: theme.secondaryColor
                    },
                    [formatFn(dateValue || new Date(), "yyyy-MM-dd")]: {
                      selected: true,
                      selectedColor: theme.calendarTodayColor || theme.secondaryColor
                    }
                  }}
                  renderArrow={direction => (
                    <Icon
                      name={`chevron-${direction}`}
                      color={theme.calendarTitleColor || theme.fontColor}
                    />
                  )}
                  onDayPress={day =>
                    this.onChangeDate(
                      new Date(day.year, day.month - 1, day.day)
                    )
                  }
                  style={{ paddingTop: 5, height: 352 }}
                  theme={{
                    backgroundColor: theme.dialogBackgroundColor,
                    calendarBackground: theme.dialogBackgroundColor,
                    monthTextColor: theme.calendarMonthColor || theme.fontColor,
                    dayTextColor: theme.calendarDayColor || theme.fontColor,
                    textSectionTitleColor:
                      theme.calendarTitleColor || theme.disabledFontColor,
                    arrowColor:
                      theme.calendarTitleColor || theme.disabledFontColor,
                    textDisabledColor: theme.disabledFontColor,
                    todayTextColor: theme.fieldFontColor || theme.fontColor
                  }}
                  {...calendarProps}
                />
              </Dialog>
            </React.Fragment>
          );
        }}
      </LocaleContext.Consumer>
    );
  }
}

DateField.defaultProps = {
  editable: true,
  parseFormat: "yyyy-MM-dd",
  format: DateFormat.shortDate
};

export default injectIntl(withTheme(DateField));
